import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { COPY } from '@/lib/copyTokens';
import { apiClient } from '@/services/apiClient';
import { useTenantStore } from '@/stores/useTenantStore';
import type { TenantSettings } from '@/types/tenant';

/**
 * Shared section-form record access.
 *
 * Every settings section reads the same tenant_settings record, but this hook
 * deliberately accepts a caller-built payload so a stale form can never write
 * fields owned by another rail section.
 */
export function useSectionTenantSettings() {
  const tenantId = useTenantStore((state) => state.currentTenant);
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const query = useQuery({
    queryKey: ['tenantSettings', tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const response = await apiClient.get<TenantSettings>(
        `/tenant-settings/${encodeURIComponent(tenantId)}`,
      );
      return response.data;
    },
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  const save = useCallback(
    async (payload: Partial<TenantSettings>): Promise<boolean> => {
      if (!tenantId) return false;
      setIsSaving(true);
      try {
        const response = await apiClient.patch<TenantSettings>(
          `/tenant-settings/${encodeURIComponent(tenantId)}`,
          payload,
        );
        queryClient.setQueryData(['tenantSettings', tenantId], response.data);
        return true;
      } catch (error) {
        console.error('Failed to save tenant settings', error);
        message.error(COPY.saveFailed);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [queryClient, tenantId],
  );

  return {
    tenantId,
    tenantSettings: query.data,
    isLoading: query.isLoading,
    isSaving,
    save,
  };
}
